import { Box, Container } from "@mui/material";
import React, { Suspense, useState } from "react";
import { Outlet, ScrollRestoration } from "react-router-dom";

import PageLoader from "../../../shared/components/PageLoader/PageLoader";
import styles from "./Dashboard.module.scss";
import Navbar from "./Navbar/Navbar";
import Sidebar from "./Sidebar/Sidebar";
import { classnames } from "../../../utils";
import useNavigationLink from "../../../hooks/useNavigationLink";
import useGlobalHotkeys from "../../../features/HotkeySettings/hooks/useGlobalHotkeys";
import RoleBasedRedirect from "../../../guards/RoleBasedRedirect";
import useUser from "../../../hooks/useUser";
import Permissions from "../../../shared/enums/Permissions";
import TenantSelector from "../../../features/TenantSelection/components/TenantSelector";
import useQueryParamActions from "../../../hooks/useQueryParamActions";
import AccessRestriction from "../../../shared/components/AccessRestriction/AccessRestriction";
import { GargiProvider } from "../../../features/RevenueIntelligence/components/AskGargi/AskGargi";

const Dashboard = () => {
  const { fullScreenMode, flushMode } = useNavigationLink();
  const [isSidebarActive, setIsSidebarActive] = useState(false);
  const { userPermissions, selectedBusiness, subscribedPermissions, currentUser } = useUser();

  useGlobalHotkeys();
  useQueryParamActions();

  const showChan = userPermissions.includes(Permissions.GARGI);

  const handleToggleSidebar = (): void => {
    setIsSidebarActive((isSidebarActive) => !isSidebarActive);
  };

  return (
    <RoleBasedRedirect>
      <Box id={styles.dashboard}>
        <TenantSelector />
        <Navbar
          onToggleSidebar={handleToggleSidebar}
          showChan={showChan}
        />
        <Box
          className={classnames(styles.app, {
            [styles.fullScreenMode]: fullScreenMode,
          })}
        >
          <Sidebar
            isSidebarActive={isSidebarActive}
            onCloseSidebar={() => setIsSidebarActive(false)}
          />
          <Container component="main" className={classnames(styles.container, { [styles.flushContainer]: flushMode })}>
            {selectedBusiness?.isActive === false ? (
              <AccessRestriction
                reason="BUSINESS_INACTIVE"
                businessName={selectedBusiness.name}
                userEmail={currentUser?.email}
              />
            ) : subscribedPermissions.length === 0 ? (
              <AccessRestriction
                reason="NO_SUBSCRIPTION"
                businessName={selectedBusiness?.name}
                userEmail={currentUser?.email}
              />
            ) : (
              // Ask Gargi on every page (for people with Gargi access): a floating button, and every screen's own
              // "Ask Gargi" buttons open the same drawer.
              <GargiProvider floating>
                <Suspense fallback={<PageLoader />}>
                  <Outlet />
                </Suspense>
              </GargiProvider>
            )}
          </Container>
        </Box>
        <ScrollRestoration />
      </Box>
    </RoleBasedRedirect>
  );
};

export default Dashboard;